import {
  type MutableRefObject,
  type ReactNode,
  useRef,
  Children,
  cloneElement,
  isValidElement,
} from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, type Transition } from 'framer-motion';
import { CaretLeft, CaretRight, IconButton } from '@hungryroot/ui-kit';
import {
  useCarousel,
  getRenderedSlideCount,
  type UseCarouselReturn,
  type ResponsiveConfig,
} from './useCarousel';
import styles from './Carousel.module.css';

// Default spring transition for slide animations
const DEFAULT_TRANSITION: Transition = {
  type: 'spring',
  mass: 2,
  stiffness: 400,
  damping: 50,
};

// --- Carousel Controls (exposed via carouselRef prop) ---

export type CarouselControls = {
  next: () => void;
  back: () => void;
  goToSlide: (index: number) => void;
  startAutoPlay: () => void;
  stopAutoPlay: () => void;
  isNextDisabled: boolean;
  isBackDisabled: boolean;
  isDragging: boolean;
  isAutoPlayEnabled: boolean;
  isInfinite: boolean;
  totalSlides: number;
  activeIndexes: number[];
  firstVisibleIndex: number;
  slidesToShow: number;
  slidesToScroll: number;
};

// --- Root Carousel Component ---

type CarouselRootProps = {
  testid?: string;
  children: ReactNode | ((carousel: UseCarouselReturn) => ReactNode);
  totalSlides: number;
  slidesToShow?: number;
  slidesToScroll?: number;
  desiredSlideWidth?: number;
  sizingGap?: number;
  responsive?: ResponsiveConfig[];
  infinite?: boolean;
  snapLastSlideToEnd?: boolean;
  autoPlay?: boolean;
  autoPlayDuration?: number;
  onSlideChange?: () => void;
  onNext?: () => void;
  onBack?: () => void;
  className?: string;
  ariaLabel?: string;
  carouselRef?: MutableRefObject<CarouselControls | null>;
};

export function Carousel({
  testid,
  children,
  totalSlides,
  slidesToShow = 1,
  slidesToScroll,
  desiredSlideWidth,
  sizingGap = 0,
  responsive,
  infinite = false,
  snapLastSlideToEnd = false,
  autoPlay = false,
  autoPlayDuration,
  onSlideChange,
  onNext,
  onBack,
  className,
  ariaLabel = 'Carousel',
  carouselRef,
}: CarouselRootProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const carousel = useCarousel({
    containerRef,
    totalSlides,
    slidesToShow,
    slidesToScroll,
    desiredSlideWidth,
    sizingGap,
    responsive,
    infinite,
    snapLastSlideToEnd,
    autoPlay,
    autoPlayDuration,
    onSlideChange,
    onNext,
    onBack,
  });

  // Helper to compute next state values for synchronous ref updates
  const getNextState = (direction: 'next' | 'back') => {
    const currentFirst = carousel.firstVisibleIndex;
    const scrollAmount = carousel.slidesToScroll;
    const maxFirst = carousel.maxFirstIndex;

    let newFirst: number;
    if (infinite) {
      // In infinite mode, compute raw position then wrap to valid range
      const rawFirst =
        direction === 'next'
          ? currentFirst + scrollAmount
          : currentFirst - scrollAmount;
      // Wrap to valid range [0, totalSlides)
      newFirst = ((rawFirst % totalSlides) + totalSlides) % totalSlides;
    } else {
      newFirst =
        direction === 'next'
          ? Math.min(currentFirst + scrollAmount, maxFirst)
          : Math.max(currentFirst - scrollAmount, 0);
    }

    // Generate activeIndexes with proper wrapping for infinite mode
    const activeIndexes = Array.from(
      { length: getRenderedSlideCount(carousel.slidesToShow) },
      (_, i) =>
        infinite
          ? (((newFirst + i) % totalSlides) + totalSlides) % totalSlides
          : newFirst + i
    );

    return {
      firstVisibleIndex: newFirst,
      activeIndexes,
      // In infinite mode, never disabled
      isNextDisabled: infinite ? false : newFirst >= maxFirst,
      isBackDisabled: infinite ? false : newFirst <= 0,
    };
  };

  // Assign controls to ref prop (updates on every render to keep values fresh)
  if (carouselRef) {
    carouselRef.current = {
      next: () => {
        // Update ref synchronously so consuming component has fresh values
        const nextState = getNextState('next');
        if (carouselRef.current) {
          carouselRef.current.firstVisibleIndex = nextState.firstVisibleIndex;
          carouselRef.current.activeIndexes = nextState.activeIndexes;
          carouselRef.current.isNextDisabled = nextState.isNextDisabled;
          carouselRef.current.isBackDisabled = nextState.isBackDisabled;
        }
        carousel.next();
      },
      back: () => {
        // Update ref synchronously so consuming component has fresh values
        const nextState = getNextState('back');
        if (carouselRef.current) {
          carouselRef.current.firstVisibleIndex = nextState.firstVisibleIndex;
          carouselRef.current.activeIndexes = nextState.activeIndexes;
          carouselRef.current.isNextDisabled = nextState.isNextDisabled;
          carouselRef.current.isBackDisabled = nextState.isBackDisabled;
        }
        carousel.back();
      },
      goToSlide: carousel.goToSlide,
      startAutoPlay: () => {
        // Update ref synchronously so consuming component has fresh values
        if (carouselRef.current) {
          carouselRef.current.isAutoPlayEnabled = true;
        }
        carousel.startAutoPlay();
      },
      stopAutoPlay: () => {
        // Update ref synchronously so consuming component has fresh values
        if (carouselRef.current) {
          carouselRef.current.isAutoPlayEnabled = false;
        }
        carousel.stopAutoPlay();
      },
      isNextDisabled: carousel.isNextDisabled,
      isBackDisabled: carousel.isBackDisabled,
      isDragging: carousel.isDragging,
      isAutoPlayEnabled: carousel.isAutoPlayEnabled,
      isInfinite: carousel.isInfinite,
      totalSlides: carousel.totalSlides,
      activeIndexes: carousel.activeIndexes,
      firstVisibleIndex: carousel.firstVisibleIndex,
      slidesToShow: carousel.slidesToShow,
      slidesToScroll: carousel.slidesToScroll,
    };
  }

  return (
    <div
      data-testid={testid}
      ref={containerRef}
      className={clsx(styles.container, className)}
      role="group"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
    >
      {typeof children === 'function' ? children(carousel) : children}
    </div>
  );
}

// --- Carousel Track ---

type CarouselTrackProps = {
  children: ReactNode;
  carousel?: UseCarouselReturn; // Required for infinite scrolling
  gap?: number;
  className?: string;
};

export function CarouselTrack({
  children,
  carousel,
  gap = 0,
  className,
}: CarouselTrackProps) {
  const childArray = Children.toArray(children);
  const isInfinite = carousel?.isInfinite ?? false;
  const cloneCount = carousel?.cloneCount ?? 0;

  let finalChildren = childArray;

  if (isInfinite && cloneCount > 0 && childArray.length > 0) {
    // Get the last N children for prepend clones
    const prependClones = childArray.slice(-cloneCount).map((child, i) => {
      if (isValidElement(child)) {
        return cloneElement(child, {
          key: `prepend-${i}`,
          isClone: true,
        } as Partial<CarouselSlideProps>);
      }
      return child;
    });

    // Get the first N children for append clones
    const appendClones = childArray.slice(0, cloneCount).map((child, i) => {
      if (isValidElement(child)) {
        return cloneElement(child, {
          key: `append-${i}`,
          isClone: true,
        } as Partial<CarouselSlideProps>);
      }
      return child;
    });

    finalChildren = [...prependClones, ...childArray, ...appendClones];
  }

  return (
    <div className={clsx(styles.carousel, className)} style={{ gap }}>
      <AnimatePresence>{finalChildren}</AnimatePresence>
    </div>
  );
}

// --- Carousel Slide ---

type CarouselSlideProps = {
  carousel: UseCarouselReturn;
  index: number;
  children: ReactNode;
  gap?: number;
  className?: string;
  transition?: Transition;
  dragElastic?: number; // 0 = no movement, 0.5 = default resistance, 1 = 1:1 tracking
  isClone?: boolean;
  /**
   * Fixed pixel width per slide. When set, slides render at exactly
   * `slideWidth`px and the track translates in pixels (not percentages).
   * Use this when you want slides of a consistent visual size — e.g.
   * product cards that should not stretch on wide viewports — instead
   * of the default behavior that divides the container by `slidesToShow`.
   */
  slideWidth?: number;
};

export function CarouselSlide({
  carousel,
  index,
  children,
  gap = 0,
  className,
  transition = DEFAULT_TRANSITION,
  dragElastic,
  isClone = false,
  slideWidth,
}: CarouselSlideProps) {
  const {
    totalSlides,
    activeIndexes,
    internalIndex,
    slidesToShow,
    isInfinite,
    shouldSkipAnimation,
    dragX,
    onDragStart,
    onDragEnd,
    onAnimationComplete,
  } = carousel;

  // Use activeIndexes (logical indices) to determine if this slide is active
  const isActive = activeIndexes.includes(index);

  const isFixedWidth = slideWidth !== undefined;

  // Fixed-width mode: size in pixels, translate in pixels. This lets slides
  // hold a consistent size regardless of container width (e.g. product
  // cards that shouldn't stretch on desktop).
  //
  // Percentage mode (default): slides divide the container evenly according
  // to `slidesToShow`, translating in percent units so content reflows
  // across breakpoints.
  const widthStyle = isFixedWidth
    ? `${slideWidth}px`
    : `calc(${100 / slidesToShow}% - ${
        (gap * (slidesToShow - 1)) / slidesToShow
      }px)`;

  const maxTranslateIndex = Math.max(0, totalSlides - slidesToShow);
  const translateIndex = isInfinite
    ? internalIndex
    : Math.min(internalIndex, maxTranslateIndex);

  const translateStyle = isFixedWidth
    ? `-${translateIndex * (slideWidth + gap)}px`
    : `calc(-${translateIndex * 100}% - ${translateIndex * gap}px)`;

  return (
    <motion.div
      className={clsx(styles.slide, className, {
        [styles.active]: isActive,
      })}
      role="group"
      aria-roledescription="slide"
      aria-label={isClone ? undefined : `${index + 1} of ${totalSlides}`}
      aria-hidden={isClone ? 'true' : undefined}
      style={{
        width: widthStyle,
        x: dragX,
      }}
      animate={{
        translateX: translateStyle,
      }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={dragElastic}
      transition={shouldSkipAnimation ? { duration: 0 } : transition}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onAnimationComplete={onAnimationComplete}
    >
      {children}
    </motion.div>
  );
}

// --- Carousel Navigation ---

type CarouselNavigationProps = {
  carousel: UseCarouselReturn;
  className?: string;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
};

export function CarouselNavigation({
  carousel,
  className,
  prevAriaLabel = 'Previous slide',
  nextAriaLabel = 'Next slide',
}: CarouselNavigationProps) {
  const { next, back, isNextDisabled, isBackDisabled } = carousel;

  if (carousel.isBackDisabled && carousel.isNextDisabled) {
    return null;
  }

  return (
    <div className={clsx(styles.nav, className)}>
      <IconButton
        style={isBackDisabled ? 'disabled' : 'muted'}
        onClick={back}
        size="small"
        prominence="secondary"
        icon={<CaretLeft />}
        ariaLabel={prevAriaLabel}
      />
      <IconButton
        style={isNextDisabled ? 'disabled' : 'muted'}
        onClick={next}
        size="small"
        prominence="secondary"
        icon={<CaretRight />}
        ariaLabel={nextAriaLabel}
      />
    </div>
  );
}

// --- Carousel Progress ---

type CarouselProgressProps = {
  carousel: UseCarouselReturn;
  className?: string;
  isVisualOnly?: boolean;
};

export function CarouselProgress({
  carousel,
  className,
  isVisualOnly,
}: CarouselProgressProps) {
  const { firstVisibleIndex, totalSlides, slidesToShow, goToSlide } = carousel;
  const { slidesToScroll } = carousel;

  const numPages =
    Math.ceil(Math.max(0, totalSlides - slidesToShow) / slidesToScroll) + 1;
  const currentPage = Math.round(firstVisibleIndex / slidesToScroll);

  if (carousel.isBackDisabled && carousel.isNextDisabled) {
    return null;
  }

  return (
    <div
      className={clsx(styles.progress, className)}
      aria-hidden={isVisualOnly ? 'true' : undefined}
    >
      {Array.from({ length: numPages }, (_, pageIndex) =>
        isVisualOnly ? (
          <div
            key={pageIndex}
            className={clsx(styles['progress-button'], {
              [styles['is-active']]: currentPage === pageIndex,
            })}
          />
        ) : (
          <button
            key={pageIndex}
            onClick={() => goToSlide(pageIndex * slidesToScroll)}
            name={`slide ${pageIndex + 1}`}
            aria-label={`Go to slide ${pageIndex + 1}`}
            aria-current={currentPage === pageIndex ? 'true' : undefined}
            className={clsx(styles['progress-button'], {
              [styles['is-active']]: currentPage === pageIndex,
            })}
          />
        )
      )}
    </div>
  );
}
